import type {
  AuthoringEnvironmentClientInfo,
  EnvironmentKey,
  Nullable,
} from '@web-config-app/core';
import { useAuthoringEnvironments } from '../use-authoring-environments/use-authoring-environments';
import { UnknownEnvironmentError } from '../../errors/unknown-environment-error';

export interface AuthoringEnvironmentLookup {
  environmentMap: Nullable<Map<EnvironmentKey, AuthoringEnvironmentClientInfo>>;
  baseAuthoringEnvironment: AuthoringEnvironmentClientInfo;
  multiEnvironmentAuthoringEnabled: boolean;
}

/**
 * Resolves an environment key to its client info. Sole owner of that rule: with
 * multi-environment authoring off, or no key given, every request goes to the base
 * environment; otherwise the key must name a known environment.
 *
 * A plain function rather than a hook, because nothing about it needs to be one --
 * `useAuthoringEnvironments()` takes no arguments, so the subscription is identical whichever
 * environment is asked about and only this lookup differs. That's what lets a caller resolve
 * an environment chosen at call time (see useEnvironmentFetchFactory) without the rule being
 * reimplemented outside the hook.
 */
export const resolveAuthoringEnvironmentInfo = (
  environment: EnvironmentKey | undefined,
  {
    environmentMap,
    baseAuthoringEnvironment,
    multiEnvironmentAuthoringEnabled,
  }: AuthoringEnvironmentLookup,
): AuthoringEnvironmentClientInfo => {
  if (!environment || !multiEnvironmentAuthoringEnabled) {
    return baseAuthoringEnvironment;
  }

  const foundEnvironment = environmentMap?.get(environment);

  if (!foundEnvironment) {
    throw new UnknownEnvironmentError(environment);
  }

  return foundEnvironment;
};

export const useAuthoringEnvironmentInfo = (environment?: EnvironmentKey) => {
  const {
    environmentMap,
    baseAuthoringEnvironment,
    multiEnvironmentAuthoringEnabled,
  } = useAuthoringEnvironments();

  return resolveAuthoringEnvironmentInfo(environment, {
    environmentMap,
    baseAuthoringEnvironment,
    multiEnvironmentAuthoringEnabled,
  });
};
